import { Component, OnInit } from '@angular/core';
import { DynamicFilters, ProductService } from '../../services/products.service';
import { CommonModule } from '@angular/common';
import { debounceTime, Observable, Subject } from 'rxjs';
import { ResponseProductFilters } from '../../types/ResponseProductFilters';
import { NgbDropdown, NgbDropdownMenu, NgbDropdownToggle, NgbPagination } from '@ng-bootstrap/ng-bootstrap';
import { PagedResponse } from '../../types/PagedResponse';
import { Product } from '../../types/Product';
import { FormsModule } from '@angular/forms';
import { stablePrioritySort } from './stablePrioritySort';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { LoadingComponent } from '@digi-america/shared';
import { SessionService } from '../../services/session.service';
import { QuoteService } from '../../services/quotes.service';
import { PRODUCT_DEFINITIONS } from '../../types/product-definitions';
import { FeedbackService } from '../../components/feedback/feedback.service';
import { ProductCardComponent } from '../../components/product-card/product-card.component';

type DropdownFilterOption = {
  value: string;
  count: number;
  dimmed: boolean;
  selected: boolean;
};

@Component({
  selector: 'app-product-search',
  standalone: true,
  imports: [CommonModule, FormsModule, NgbPagination, NgbDropdown, NgbDropdownMenu, NgbDropdownToggle, LoadingComponent, RouterLink, ProductCardComponent],
  templateUrl: './product-search.component.html',
  styleUrl: './product-search.component.scss'
})
export class ProductSearchComponent implements OnInit {

  public quoteId?: string = undefined;

  public productType!: string;
  public allFilters$!: Observable<ResponseProductFilters>;
  public currentFilters$!: Observable<ResponseProductFilters>;

  public products$!: Observable<PagedResponse<Product>>;

  public allFilters!: ResponseProductFilters;
  public currentFilters!: ResponseProductFilters;

  public searchInput$: Subject<string> = new Subject<string>();
  public searchValue: string = '';

  public page: number = 1;

  public selectedFilters: DynamicFilters = {};
  public productName!: string;

  constructor(private productsService: ProductService,
    private route: ActivatedRoute,
    private router: Router,
    private sessionService: SessionService,
    private quoteService: QuoteService,
    private feedbackService: FeedbackService) {

  }

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      this.quoteId = params['quoteId'];
    });

    this.route.params.subscribe(params => {
      this.productType = params["type"];
      this.productName = PRODUCT_DEFINITIONS.find(pd => pd.id === this.productType)?.label ?? "Unknown";

      this.searchInput$.pipe(debounceTime(500)).subscribe((searchTerm) => {
        this.searchValue = searchTerm;
        this.updateSearch(1);
      });

      this.allFilters$ = this.productsService.productFilters(this.productType);
      this.allFilters$.subscribe({
        next:
          (response) => {
            this.allFilters = response;
            this.currentFilters = response;

            this.allFilters.filters.sort((a, b) => a.order - b.order)
          }
      });

      this.updateSearch(this.page);
    });
  }

  ngOnDestroy() {
    this.searchInput$.complete();
  }

  public updateSearch(page: number): void {
    this.page = page;
    this.products$ = this.productsService.searchProduct(this.productType, this.selectedFilters, this.searchValue, "sku", page, 10);
  }

  getOptionsFor(key: string): Array<DropdownFilterOption> {
    let baseFilter = this.allFilters.filters.find(f => f.key === key);
    let activeFilter = this.currentFilters.filters.find(f => f.key === key);

    let results = baseFilter?.options
      .filter(f => f.value)
      .map(f => {
        let aF = activeFilter?.options.find(a => a.value === f.value);
        let selected = (this.selectedFilters[key] || []).indexOf(f.value) !== -1;

        return {
          value: f.value,
          count: aF ? aF.count : 0,
          dimmed: !aF || aF.count === 0,
          selected
        }
      });

    return stablePrioritySort(results || [], this.selectedFilters[key] || [], (a: DropdownFilterOption) => a.value);
  }

  updateSelectedFilter(key: string, value: string, event: Event): void {
    const checkbox = event.target as HTMLInputElement;
    const newValue = checkbox.checked;

    if (newValue) {
      if (!Array.isArray(this.selectedFilters[key])) {
        this.selectedFilters[key] = [value];
      }
      else {
        if (this.selectedFilters[key].indexOf(value) === -1) {
          this.selectedFilters[key].push(value);
        }
      }
    }
    else {
      let index = this.selectedFilters[key] ? this.selectedFilters[key].indexOf(value) : -1;
      if (index >= 0) {
        this.selectedFilters[key].splice(index, 1);
      }

    }

    this.updateSearch(1);
    this.currentFilters$ = this.productsService.productFilters(this.productType, this.selectedFilters);
    this.currentFilters$.subscribe({
      next: (value) => {
        this.currentFilters = value;
      }
    });
  }

  hasSelectedFilter(key: string, value?: string): boolean {
    const selected = this.selectedFilters[key];
    if (!Array.isArray(selected)) {
      return false;
    }
    else {
      if (selected.length === 0)
        return false;
      else if (value === undefined) {
        return true;
      }
      else {
        return selected.indexOf(value) >= 0;
      }
    }
  }

  hasCreatePermissions(): boolean {
    return this.sessionService.hasPermission("product:create");
  }
}